(() => {
  const FIND_STORE_CTA_COUNTED_KEY = 'waisa:find-store-cta-counted';

  if (
    (window.__waisaFindNearestRequested || window.location.hash === '#near-me') &&
    typeof window.__waisaRequestUserLocation === 'function'
  ) {
    window.__waisaRequestUserLocation();
  }

  const MAP_STYLES = [
    {
      featureType: 'all',
      elementType: 'geometry',
      stylers: [{ saturation: -100 }, { lightness: 8 }],
    },
    { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ color: '#4f4f4f' }] },
    { featureType: 'all', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
    { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#e9ecf3' }] },
    { featureType: 'landscape', elementType: 'geometry.fill', stylers: [{ color: '#f8f9fc' }] },
    { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e0e4ed' }] },
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ];

  const PIN_SVG =
    'M12 0C7.31 0 3.5 3.81 3.5 8.5c0 6.38 8.5 14.5 8.5 14.5s8.5-8.12 8.5-14.5C20.5 3.81 16.69 0 12 0zm0 12a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z';

  const SANITIZE_FIELDS = [
    'name',
    'address',
    'city',
    'state',
    'zip',
    'country',
    'phone',
    'website',
  ];

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function normalizeStreet(value) {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[.]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\b(street)\b/g, 'st')
      .replace(/\b(avenue)\b/g, 'ave')
      .replace(/\b(road)\b/g, 'rd')
      .replace(/\b(drive)\b/g, 'dr')
      .replace(/\b(boulevard)\b/g, 'blvd')
      .replace(/\b(north)\b/g, 'n')
      .replace(/\b(south)\b/g, 's')
      .replace(/\b(east)\b/g, 'e')
      .replace(/\b(west)\b/g, 'w')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function placeKey(store) {
    const address = normalizeStreet(store.address);
    if (!address) return `id:${store.id || store.name}`;
    const city = store.city === 'North York' ? 'Toronto' : store.city;
    return [address, normalizeStreet(city)].join('|');
  }

  function uniqueStores(sourceStores) {
    const storesById = new Map();
    sourceStores.forEach((store) => {
      if (!store || !store.name) return;
      if (/\bCLOSED\b/i.test(store.name)) return;
      const storeKey =
        store.id ||
        [store.name, store.address, store.city, store.state, store.zip, store.country].join('|');
      if (storesById.has(storeKey)) return;
      storesById.set(storeKey, {
        ...store,
        products: Array.isArray(store.products) ? [...store.products] : [],
      });
    });

    const storesByPlace = new Map();
    storesById.forEach((store) => {
      const key = placeKey(store);
      const existing = storesByPlace.get(key);
      if (!existing || String(store.name).length < String(existing.name).length) {
        storesByPlace.set(key, store);
      }
    });
    return Array.from(storesByPlace.values());
  }

  function normalizeProductUrl(url) {
    if (!url) return '';
    const normalizedUrl = url
      .split('?')[0]
      .split('#')[0]
      .replace(/^https?:\/\/[^/]+/i, '');
    if (!normalizedUrl) return '';
    return (normalizedUrl.startsWith('/') ? normalizedUrl : `/${normalizedUrl}`).replace(
      /\/+$/,
      ''
    );
  }

  function distanceKm(lat1, lng1, lat2, lng2) {
    const rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad;
    const dLng = (lng2 - lng1) * rad;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatAddressLine(store) {
    const stateZip = [store.state, store.zip].filter(Boolean).join(' ');
    return [store.city, stateZip].filter(Boolean).join(', ');
  }

  function getFullAddress(store) {
    return [
      store.address,
      store.city,
      [store.state, store.zip].filter(Boolean).join(' '),
      store.country || 'Canada',
    ]
      .filter(Boolean)
      .join(', ');
  }

  function directionsUrl(store) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(getFullAddress(store))}`;
  }

  function parseCoord(value) {
    if (value == null || value === '' || value === 'null') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  async function geocodeAddress(geocoder, address) {
    try {
      const response = await geocoder.geocode({
        address,
        componentRestrictions: { country: 'CA' },
      });
      const location = response.results?.[0]?.geometry?.location;
      if (!location) return null;
      return { lat: location.lat(), lng: location.lng() };
    } catch {
      return null;
    }
  }

  async function runWithConcurrency(items, limit, worker) {
    const executing = new Set();
    for (const item of items) {
      const pending = Promise.resolve()
        .then(() => worker(item))
        .finally(() => executing.delete(pending));
      executing.add(pending);
      if (executing.size >= limit) await Promise.race(executing);
    }
    await Promise.all(executing);
  }

  let geocodingLibraryPromise = null;

  function ensureGeocodingLibrary() {
    if (!geocodingLibraryPromise) {
      geocodingLibraryPromise = google.maps.importLibrary('geocoding').catch((error) => {
        geocodingLibraryPromise = null;
        throw error;
      });
    }
    return geocodingLibraryPromise;
  }

  async function fillMissingCoordinates(stores) {
    const missing = stores.filter(
      (store) => parseCoord(store.lat) == null || parseCoord(store.lng) == null
    );
    if (missing.length === 0) return;
    try {
      await ensureGeocodingLibrary();
    } catch (geocodingError) {
      console.warn('Google Maps geocoding library unavailable', geocodingError);
    }
    if (typeof google === 'undefined' || typeof google.maps?.Geocoder !== 'function') {
      return;
    }
    let geocoder;
    try {
      geocoder = new google.maps.Geocoder();
    } catch (error) {
      console.warn('Google Maps Geocoder unavailable', error);
      return;
    }
    await runWithConcurrency(missing, 4, async (store) => {
      const coords = await geocodeAddress(geocoder, getFullAddress(store));
      if (!coords) return;
      store.lat = coords.lat;
      store.lng = coords.lng;
    });
  }

  async function initStoreLocator(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const shouldAutoLocate =
      window.__waisaFindNearestRequested ||
      window.location.hash === '#near-me' ||
      new URLSearchParams(window.location.search).has('near');
    if (shouldAutoLocate && typeof window.__waisaRequestUserLocation === 'function') {
      window.__waisaRequestUserLocation();
    }

    const currentProductUrl = root.dataset.currentProductUrl
      ? JSON.parse(root.dataset.currentProductUrl)
      : null;
    const collectionProducts = JSON.parse(
      root.querySelector('[data-collection-products]')?.textContent || '[]'
    );
    let stores = uniqueStores([
      ...(window.shop_metaobject_stores || []),
      ...(window.product_page_stores || []),
      ...(window.collection_page_store_candidates || []),
    ]);

    stores.forEach((store) => {
      SANITIZE_FIELDS.forEach((key) => {
        if (store[key] == null || store[key] === 'null') store[key] = '';
      });
      store.lat = parseCoord(store.lat);
      store.lng = parseCoord(store.lng);
      if (!Array.isArray(store.products)) store.products = [];
    });

    if (collectionProducts.length > 0) {
      stores.forEach((store) => {
        const existingUrls = new Set(
          store.products.map((product) => normalizeProductUrl(product.url))
        );
        collectionProducts.forEach((product) => {
          const url = normalizeProductUrl(product.url);
          if (!existingUrls.has(url)) {
            store.products.push(product);
            existingUrls.add(url);
          }
        });
      });
    }

    const countEl = root.querySelector('[data-store-count]');
    const listEl = root.querySelector('[data-store-list]');
    const statusEl = root.querySelector('[data-geo-status]');
    const searchInput = root.querySelector('[data-store-search]');
    const searchBtn = root.querySelector('[data-store-search-btn]');
    const geoBtn = root.querySelector('[data-store-near-me]');

    function publishIntent(name, params) {
      if (typeof window.__waisaPublishIntent === 'function') {
        window.__waisaPublishIntent(name, params);
      }
    }

    // The list renders before Google Maps loads, so shoppers see stores even
    // when Maps is slow or blocked. The map starts when it is near the viewport.
    const mapEl = root.querySelector('[data-store-map]');
    let map = null;
    let mapPromise = null;
    let pinIcon = null;
    let activeInfoWindow = null;
    const markers = [];
    let orderedIndexes = stores.map((_, index) => index);

    function updateCount() {
      if (!countEl) return;
      countEl.textContent = `${stores.length} location${stores.length === 1 ? '' : 's'}`;
    }

    function buildProductChips(products) {
      if (!products || products.length === 0) return '';
      const normalizedCurrent = normalizeProductUrl(currentProductUrl);
      return products
        .map((product) => {
          if (!product.image || product.image === 'null') return '';
          const isCurrent =
            normalizedCurrent && normalizeProductUrl(product.url) === normalizedCurrent;
          const chipClass = isCurrent ? 'product-chip product-chip--current' : 'product-chip';
          const thumb = `<img src="${escapeHtml(product.image)}" alt="" class="product-chip__thumb" loading="lazy" aria-hidden="true">`;
          return product.url
            ? `<a href="${escapeHtml(product.url)}" class="${chipClass}" aria-label="View product">${thumb}</a>`
            : `<span class="${chipClass}">${thumb}</span>`;
        })
        .join('');
    }

    function buildInfoWindowContent(store) {
      const phoneHtml = store.phone
        ? `<div class="info-window__phone"><a href="tel:${escapeHtml(store.phone)}">${escapeHtml(store.phone)}</a></div>`
        : '';
      const productsHtml =
        store.products.length > 0
          ? `<div class="info-window__divider"></div><div class="info-window__products">${buildProductChips(
              store.products
            )}</div>`
          : '';
      return `
        <div class="info-window">
          <div class="info-window__address">${escapeHtml(store.address)}<br>${escapeHtml(formatAddressLine(store))}</div>
          ${phoneHtml}
          <a href="${directionsUrl(store)}" target="_blank" rel="noopener" class="info-window__directions" data-waisa-intent="get_directions" data-store-name="${escapeHtml(store.name)}">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M22.43 10.59l-9.01-9.01c-.75-.75-1.99-.75-2.73 0l-9.01 9.01c-.75.75-.75 1.99 0 2.73l9.01 9.01c.75.75 1.99.75 2.73 0l9.01-9.01c.75-.74.75-1.98 0-2.73zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z"/></svg>
            Get directions
          </a>
          ${productsHtml}
        </div>
      `;
    }

    function buildStoreCard(store, index) {
      const phoneHtml = store.phone
        ? `<div class="store-card__phone"><a href="tel:${escapeHtml(store.phone)}">${escapeHtml(store.phone)}</a></div>`
        : '';
      const productsHtml =
        store.products.length > 0
          ? `<div class="store-card__products">${buildProductChips(store.products)}</div>`
          : '';
      const distance =
        store.distanceKm != null
          ? `<div class="store-card__distance" style="display:block">${store.distanceKm.toFixed(1)} km away</div>`
          : `<div class="store-card__distance"></div>`;
      return `
        <div class="store-card" data-index="${index}" id="store-card-${index}">
          <div class="store-card__name">${escapeHtml(store.name)}</div>
          <div class="store-card__address">
            ${escapeHtml(store.address)}<br>${escapeHtml(formatAddressLine(store))}
          </div>
          ${distance}
          ${phoneHtml}
          <a href="${directionsUrl(store)}" target="_blank" rel="noopener" class="store-card__directions" data-waisa-intent="get_directions" data-store-name="${escapeHtml(store.name)}">Get directions</a>
          ${productsHtml}
        </div>
      `;
    }

    function renderList() {
      if (!listEl) return;
      listEl.innerHTML = orderedIndexes
        .map((index) => buildStoreCard(stores[index], index))
        .join('');
    }

    function selectStore(index) {
      if (activeInfoWindow) activeInfoWindow.close();
      root
        .querySelectorAll('.store-card')
        .forEach((card) => card.classList.remove('store-card--active'));
      const card = document.getElementById(`store-card-${index}`);
      if (card) {
        card.classList.add('store-card--active');
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      const marker = markers[index];
      if (!marker) return;

      const headerEl = document.createElement('div');
      headerEl.className = 'info-window__header-title';
      headerEl.textContent = stores[index].name;

      const infoWindow = new google.maps.InfoWindow({
        headerContent: headerEl,
        content: buildInfoWindowContent(stores[index]),
      });
      infoWindow.open(map, marker);
      activeInfoWindow = infoWindow;
      map.panTo(marker.getPosition());
      if (map.getZoom() < 12) map.setZoom(12);
    }

    function fitToMarkers(indexes) {
      if (!map) return;
      const bounds = new google.maps.LatLngBounds();
      let count = 0;
      (indexes || stores.map((_, i) => i)).forEach((index) => {
        const marker = markers[index];
        if (!marker) return;
        bounds.extend(marker.getPosition());
        count += 1;
      });
      if (count === 0) return;
      if (count === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(12);
        return;
      }
      map.fitBounds(bounds);
    }

    function rankByDistance(lat, lng) {
      stores.forEach((store) => {
        if (store.lat == null || store.lng == null) {
          store.distanceKm = null;
          return;
        }
        store.distanceKm = distanceKm(lat, lng, store.lat, store.lng);
      });
      orderedIndexes = stores
        .map((store, index) => ({ index, km: store.distanceKm }))
        .sort((a, b) => {
          if (a.km == null && b.km == null) return 0;
          if (a.km == null) return 1;
          if (b.km == null) return -1;
          return a.km - b.km;
        })
        .map((item) => item.index);
      renderList();
      const closest = orderedIndexes.find(
        (index) => stores[index].lat != null && stores[index].lng != null
      );
      if (closest == null) return;
      selectStore(closest);
      const nearby = orderedIndexes.filter((index) => stores[index].distanceKm != null).slice(0, 8);
      fitToMarkers(nearby);
    }

    function setStatus(message) {
      if (!statusEl) return;
      statusEl.hidden = !message;
      statusEl.textContent = message || '';
    }

    function setBusy(isBusy, label) {
      if (geoBtn) {
        geoBtn.disabled = isBusy;
        if (label) geoBtn.textContent = label;
      }
      if (searchBtn) searchBtn.disabled = isBusy;
    }

    function addMarker(store, index) {
      if (!map || store.lat == null || store.lng == null || markers[index]) return;
      const position = new google.maps.LatLng(store.lat, store.lng);
      const marker = new google.maps.Marker({
        map,
        position,
        title: store.name,
        icon: pinIcon,
      });
      marker.addListener('click', () => selectStore(index));
      markers[index] = marker;
    }

    updateCount();
    renderList();
    listEl?.addEventListener('click', (event) => {
      if (event.target.closest?.('[data-waisa-intent]')) return;
      const card = event.target.closest('.store-card');
      if (!card) return;
      selectStore(parseInt(card.dataset.index, 10));
    });

    function fitMapToPins() {
      if (markers.filter(Boolean).length === 0) return;
      fitToMarkers();
      const listener = google.maps.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
        google.maps.event.removeListener(listener);
      });
    }

    let userCoords = null;

    async function initMap() {
      await waitForMapsBootstrap();
      await google.maps.importLibrary('maps');
      ensureGeocodingLibrary().catch((error) => {
        console.warn('Google Maps geocoding library unavailable', error);
      });

      pinIcon = {
        path: PIN_SVG,
        fillColor: '#000000',
        fillOpacity: 1,
        strokeColor: '#e9ecf3',
        strokeWeight: 1.5,
        scale: 1.6,
        anchor: new google.maps.Point(12, 23),
      };
      map = new google.maps.Map(mapEl, {
        zoom: 4,
        center: { lat: 53.4, lng: -93.0 },
        styles: MAP_STYLES,
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: true,
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
      });

      stores.forEach((store, index) => addMarker(store, index));
      if (userCoords) {
        rankByDistance(userCoords.lat, userCoords.lng);
      } else {
        fitMapToPins();
      }

      fillMissingCoordinates(stores)
        .then(() => {
          stores.forEach((store, index) => addMarker(store, index));
          if (userCoords) {
            rankByDistance(userCoords.lat, userCoords.lng);
            return;
          }
          fitMapToPins();
        })
        .catch((error) => {
          console.warn('Could not geocode remaining stores', error);
        });
    }

    function startMap() {
      if (!mapPromise) {
        mapPromise = initMap().catch((error) => {
          console.error('Store locator map failed to load', error);
        });
      }
      return mapPromise;
    }

    function consumeFindStoreCtaDedup() {
      const fromWindow = Boolean(window.__waisaFindNearestRequested);
      window.__waisaFindNearestRequested = false;
      let fromSession = false;
      try {
        fromSession = sessionStorage.getItem(FIND_STORE_CTA_COUNTED_KEY) === '1';
        if (fromSession) sessionStorage.removeItem(FIND_STORE_CTA_COUNTED_KEY);
      } catch {
        // Ignore private-mode failures.
      }
      return fromWindow || fromSession;
    }

    function applyUserLocation(lat, lng, method = 'geolocation', { skipFindStore = false } = {}) {
      userCoords = { lat, lng };
      rankByDistance(lat, lng);
      setBusy(false, 'Update my location');
      setStatus('Showing the closest stores to you.');
      if (method !== 'geolocation' || !skipFindStore) {
        publishIntent('find_store', { method });
      }
    }

    function locateUser(event) {
      const ctaAlreadyCounted = consumeFindStoreCtaDedup();
      const skipFindStore = ctaAlreadyCounted || event?.type === 'waisa:find-nearest-store';
      if (typeof window.__waisaRequestUserLocation !== 'function' && !navigator.geolocation) {
        setStatus(
          'Location is not supported in this browser. Search a city or postal code instead.'
        );
        return;
      }
      startMap();
      setBusy(true, 'Locating…');
      setStatus('');
      const request =
        typeof window.__waisaRequestUserLocation === 'function'
          ? window.__waisaRequestUserLocation()
          : new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(
                (position) =>
                  resolve({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  }),
                reject,
                { timeout: 10000, maximumAge: 300000, enableHighAccuracy: false }
              );
            });
      request.then(
        (coords) => applyUserLocation(coords.lat, coords.lng, 'geolocation', { skipFindStore }),
        () => {
          setBusy(false, 'Find store near me');
          setStatus('Location unavailable. Search a city or postal code instead.');
        }
      );
    }

    geoBtn?.addEventListener('click', locateUser);
    window.addEventListener('waisa:find-nearest-store', locateUser);

    if (shouldAutoLocate) {
      locateUser();
    }

    async function searchAddress(query) {
      const trimmed = query.trim();
      if (!trimmed) {
        setStatus('Enter a city or postal code to search.');
        return;
      }
      startMap();
      try {
        await ensureGeocodingLibrary();
      } catch (error) {
        console.warn('Google Maps geocoding library unavailable', error);
      }
      if (typeof google === 'undefined' || typeof google.maps?.Geocoder !== 'function') {
        setStatus('Search is unavailable. Try Find store near me instead.');
        return;
      }
      setBusy(true);
      setStatus('');
      try {
        const geocoder = new google.maps.Geocoder();
        const coords = await geocodeAddress(geocoder, trimmed);
        if (!coords) {
          setStatus('Could not find that place. Try a city or postal code in Canada.');
          return;
        }
        applyUserLocation(coords.lat, coords.lng, 'search');
        setStatus(`Closest stores to ${trimmed}.`);
      } finally {
        setBusy(false, geoBtn?.textContent || 'Find store near me');
      }
    }

    searchBtn?.addEventListener('click', () => searchAddress(searchInput?.value || ''));
    searchInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      searchAddress(searchInput.value);
    });

    if (mapEl && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          startMap();
        },
        { rootMargin: '200px 0px' }
      );
      observer.observe(mapEl);
    } else if (mapEl) {
      startMap();
    }
  }

  function waitForMapsBootstrap(timeoutMs = 15000) {
    if (typeof google !== 'undefined' && google.maps?.importLibrary) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const started = Date.now();
      const timer = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps?.importLibrary) {
          clearInterval(timer);
          resolve();
        } else if (Date.now() - started > timeoutMs) {
          clearInterval(timer);
          resolve();
        }
      }, 50);
    });
  }

  async function boot() {
    const root = document.querySelector('[data-store-locator]');
    if (!root) return;
    try {
      await initStoreLocator(root);
    } catch (error) {
      console.error('Store locator failed to load', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
